import type { PostProcedureOutcomeRoute } from "./postProcedureOutcome";
import type { CycleOutcome } from "../hooks/useCycles";

/**
 * Canonical state at the boundary between cycle outcomes and Home.
 *
 * The cycle API supplies the outcome identity, clinical result, provenance,
 * verification, and the server acknowledgement timestamp. Keeping those
 * fields together prevents Home from guessing state from browser history or
 * the last visited route.
 */

export type PostProcedureOutcomeResult =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other" | null;

export type PostProcedureOutcomeSource =
  | "provider"
  | "patient"
  | "partner"
  | "system"
  | "test-result"
  | "clinic-imported"
  | "fixture"
  | "unknown";

export type PostProcedureOutcomeVerification =
  "unverified" | "verified" | "refuted" | "conflicting" | "unavailable";

export type PostProcedureOutcomeResultStatus = CycleOutcome["resultStatus"];

export type PostProcedureOutcomeStage = "recorded" | "pause" | "setup";

export type PostProcedureOutcomeCanonicalState =
  PostProcedureOutcomeStage | "unresolved" | "adverse" | "cancelled";

export type PostProcedureOutcomeClinicalState =
  "pregnancy" | "no_pregnancy" | "adverse" | "cancelled" | "unresolved";

export type PostProcedureOutcomeClinicalInput = {
  type?: string | null;
  resultStatus?: string | null;
  recordStatus?: string | null;
  verificationStatus?: string | null;
};

/** The server-side event Home is waiting for. */
export type PostProcedureOutcomeAcknowledgement = {
  outcomeId: string;
  acknowledgedAt: string;
};

/**
 * Legacy compatibility shape for callers that still need to read old review
 * fixtures. Production acknowledgement is no longer persisted with this
 * adapter; the Home hook uses the cycle API as its source of truth.
 */
export type LocalPostProcedureOutcomeAcknowledgement = {
  version: 1;
  outcomeId: string | null;
  route: PostProcedureOutcomeRoute;
  stage: Exclude<PostProcedureOutcomeStage, "recorded">;
  acknowledgedAt: string;
};

export type PostProcedureOutcomeRecord = {
  outcomeId: string | null;
  cycleId: string | null;
  result: PostProcedureOutcomeResult;
  resultStatus: PostProcedureOutcomeResultStatus;
  source: PostProcedureOutcomeSource;
  verificationStatus: PostProcedureOutcomeVerification;
  dateRecorded: string | null;
  acknowledgedAt: string | null;
  /**
   * The outcome this record replaces. Named for the API field
   * (`cycles[].outcome.supersedesOutcomeId`) rather than the reverse-sounding
   * `correctedFromOutcomeId` it used to carry, which matched no field and so was
   * always null.
   */
  supersedesOutcomeId: string | null;
  notes: string | null;
};

export type PostProcedureOutcomeRecordInput = {
  id?: string | null;
  cycleId?: string | null;
  type?: string | null;
  resultStatus?: PostProcedureOutcomeResultStatus;
  source?: string | null;
  verificationStatus?: string | null;
  dateRecorded?: string | null;
  acknowledgedAt?: string | null;
  supersedesOutcomeId?: string | null;
  notes?: string | null;
};

export function serializeLocalPostProcedureOutcomeAcknowledgement(
  value: LocalPostProcedureOutcomeAcknowledgement,
): string {
  return JSON.stringify(value);
}

/**
 * Reads the legacy local fixture shape for contract tests and migration tools.
 * It is intentionally not used as a production clinical-state source.
 */
export function parseLocalPostProcedureOutcomeAcknowledgement(
  value: string | null,
  expectedRoute: PostProcedureOutcomeRoute,
  expectedOutcomeId: string | null,
): LocalPostProcedureOutcomeAcknowledgement | null {
  if (!value) return null;

  if (value === "pause" || value === "setup") {
    return {
      version: 1,
      outcomeId: expectedOutcomeId,
      route: expectedRoute,
      stage: value,
      acknowledgedAt: "legacy-local-prototype",
    };
  }

  try {
    const parsed = JSON.parse(
      value,
    ) as Partial<LocalPostProcedureOutcomeAcknowledgement>;
    if (
      parsed.version !== 1 ||
      parsed.route !== expectedRoute ||
      parsed.outcomeId !== expectedOutcomeId ||
      (parsed.stage !== "pause" && parsed.stage !== "setup") ||
      typeof parsed.acknowledgedAt !== "string"
    ) {
      return null;
    }
    return parsed as LocalPostProcedureOutcomeAcknowledgement;
  } catch {
    return null;
  }
}

const RESULTS = new Set<Exclude<PostProcedureOutcomeResult, null>>([
  "pregnancy",
  "no_pregnancy",
  "miscarriage",
  "cancelled",
  "other",
]);

const SOURCES = new Set<PostProcedureOutcomeSource>([
  "provider",
  "patient",
  "partner",
  "system",
  "test-result",
  "clinic-imported",
  "fixture",
  "unknown",
]);

const VERIFICATION_STATUSES = new Set<PostProcedureOutcomeVerification>([
  "unverified",
  "verified",
  "refuted",
  "conflicting",
  "unavailable",
]);

function normalizeResult(
  value: string | null | undefined,
): PostProcedureOutcomeResult {
  return value &&
    RESULTS.has(value as Exclude<PostProcedureOutcomeResult, null>)
    ? (value as Exclude<PostProcedureOutcomeResult, null>)
    : null;
}

function normalizeSource(
  value: string | null | undefined,
): PostProcedureOutcomeSource {
  return value && SOURCES.has(value as PostProcedureOutcomeSource)
    ? (value as PostProcedureOutcomeSource)
    : "unknown";
}

function normalizeVerification(
  value: string | null | undefined,
): PostProcedureOutcomeVerification {
  return value &&
    VERIFICATION_STATUSES.has(value as PostProcedureOutcomeVerification)
    ? (value as PostProcedureOutcomeVerification)
    : "unverified";
}

export function normalizePostProcedureOutcomeRecord(
  input: PostProcedureOutcomeRecordInput | null | undefined,
): PostProcedureOutcomeRecord | null {
  if (!input) return null;

  return {
    outcomeId: input.id ?? null,
    cycleId: input.cycleId ?? null,
    result: normalizeResult(input.type),
    resultStatus: input.resultStatus ?? null,
    source: normalizeSource(input.source),
    verificationStatus: normalizeVerification(input.verificationStatus),
    dateRecorded: input.dateRecorded ?? null,
    acknowledgedAt: input.acknowledgedAt ?? null,
    supersedesOutcomeId: input.supersedesOutcomeId ?? null,
    notes: input.notes ?? null,
  };
}

/**
 * Resolve the clinical meaning of an outcome before any UI-specific state.
 * Explicit verification failures take precedence over the recorded result so a
 * corrected or withdrawn finding cannot be presented as fact.
 */
export function resolvePostProcedureOutcomeClinicalState(
  outcome: PostProcedureOutcomeClinicalInput | null | undefined,
): PostProcedureOutcomeClinicalState {
  if (!outcome) return "unresolved";

  if (outcome.recordStatus === "entered_in_error") return "unresolved";

  if (
    outcome.verificationStatus === "refuted" ||
    outcome.verificationStatus === "conflicting" ||
    outcome.verificationStatus === "unavailable"
  ) {
    return "unresolved";
  }

  /**
   * The patient's own word about how the cycle ended, chosen *against* a result
   * already on the record.
   *
   * On a conceiving cycle being closed now, `resolveEndCycleOptions` narrows the
   * picker once a result exists to that result's ending or `"other"` — so
   * `"other"` there is not a shrug, it is her saying the recorded result is not
   * how this ended. (It is a genuine "none of these" in the preservation,
   * donation and past-cycle flows, which never narrow; harmless, because those
   * leave `resultStatus` null and land here either way.)
   *
   * It has to sit above every `resultStatus` branch, not just the positive one.
   * Read below them, a positive beta closed as `"other"` still resolved to
   * `"pregnancy"` and Home answered the close with "Congratulations — it's
   * positive". But a recorded loss closed as `"other"` resolved to `"adverse"`
   * and answered it with condolences she had just declined, and a `"withdrawn"`
   * result with "your cycle was cancelled". Same fault, three signs. Fixing only
   * the one that was reported would have left the others asserting endings she
   * had said were not hers.
   *
   * The close cannot correct the record itself: `PATCH /{tenantId}/cycles/{id}`
   * has no `resultStatus` field — only the outcome-recording endpoint writes it —
   * so the precedence is the only place this can be settled.
   *
   * `"unresolved"` and not `"no_pregnancy"`: she declined to name the ending, and
   * inventing a negative from that would be the same overreach in the other
   * direction. She can name it: the picker offers the full vocabulary again once
   * the recorded ending is `"other"`.
   */
  if (outcome.type === "other") return "unresolved";

  if (
    outcome.type === "cancelled" ||
    outcome.resultStatus === "cancelled" ||
    outcome.resultStatus === "withdrawn"
  ) {
    return "cancelled";
  }

  if (outcome.type === "miscarriage" || outcome.resultStatus === "adverse") {
    return "adverse";
  }

  if (
    outcome.resultStatus === "unavailable" ||
    outcome.resultStatus === "uncertain" ||
    outcome.resultStatus === "pending"
  ) {
    return "unresolved";
  }

  if (
    outcome.resultStatus === "positive" ||
    outcome.resultStatus === "verified_positive"
  ) {
    return "pregnancy";
  }
  if (
    outcome.resultStatus === "negative" ||
    outcome.resultStatus === "verified_negative"
  ) {
    return "no_pregnancy";
  }

  if (outcome.type === "pregnancy") return "pregnancy";
  if (outcome.type === "no_pregnancy") return "no_pregnancy";
  return "unresolved";
}

/**
 * Resolve the only states that may drive an ordinary pause/setup transition.
 *
 * Acknowledgement alone is never enough: cancelled, adverse, refuted,
 * conflicting, unavailable, and unknown results must remain separate from the
 * positive/negative patient journey until their dedicated contracts exist.
 */
export function resolvePostProcedureOutcomeState(
  outcome: PostProcedureOutcomeRecord | null,
): PostProcedureOutcomeCanonicalState {
  if (!outcome) return "unresolved";

  const clinicalState = resolvePostProcedureOutcomeClinicalState({
    type: outcome.result,
    resultStatus: outcome.resultStatus,
    verificationStatus: outcome.verificationStatus,
  });

  if (clinicalState === "cancelled") return "cancelled";
  if (clinicalState === "adverse") return "adverse";
  if (clinicalState !== "pregnancy" && clinicalState !== "no_pregnancy") {
    return "unresolved";
  }
  if (!outcome.acknowledgedAt) return "recorded";
  return clinicalState === "pregnancy" ? "setup" : "pause";
}

/**
 * Which acknowledged surface a route may transition to, or `null` when it has
 * none.
 *
 * The acknowledgement hook used to derive this by testing whether the route
 * string ended in `-positive`, sending everything else — including `-adverse`,
 * `-uncertain` and `-unavailable` — to the negative-result pause. A loss
 * acknowledged as an ordinary failed cycle is precisely the tone error this
 * module exists to prevent, so Home had to defend itself by pinning those
 * routes to `recorded` and never letting them acknowledge at all.
 *
 * Stating the rule here instead means the constraint lives with the ending
 * vocabulary: a route with no acknowledged surface returns `null`, and a caller
 * that honours `null` needs no pin of its own.
 */
export function resolveAcknowledgedStageForRoute(
  route: PostProcedureOutcomeRoute,
): Exclude<PostProcedureOutcomeStage, "recorded"> | null {
  if (route.endsWith("-positive")) return "setup";
  if (route.endsWith("-negative")) return "pause";
  return null;
}
